import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Paper,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountTree,
  DragIndicator,
} from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: "#FCE4EC",
    borderBottom: `2px solid #E91E63`,
  },
  icon: {
    color: "#E91E63",
    width: 28,
    height: 28,
  },
  content: {
    padding: theme.spacing(3),
  },
  caseItem: {
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#FAFAFA",
    position: "relative",
    "&:hover": {
      borderColor: "#E91E63",
    },
  },
  caseHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  caseNumber: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: "bold",
    color: "#E91E63",
  },
  defaultCase: {
    padding: theme.spacing(2),
    backgroundColor: "#FFF3E0",
    borderRadius: "8px",
    border: "1px dashed #FF9800",
    marginTop: theme.spacing(2),
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  previewBox: {
    backgroundColor: "#F5F5F5",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
}));

const FlowBuilderAddSwitchModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const [variable, setVariable] = useState("");
  const [cases, setCases] = useState([
    { value: "", label: "" },
  ]);

  useEffect(() => {
    if (open === "edit" && data?.data) {
      setVariable(data.data.variable || "");
      setCases(data.data.cases || [{ value: "", label: "" }]);
    } else if (open === "create") {
      setVariable("");
      setCases([{ value: "", label: "" }]);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setVariable("");
    setCases([{ value: "", label: "" }]);
  };

  const handleAddCase = () => {
    setCases([...cases, { value: "", label: "" }]);
  };

  const handleRemoveCase = (index) => {
    if (cases.length > 1) {
      setCases(cases.filter((_, i) => i !== index));
    }
  };

  const handleCaseChange = (index, field, value) => {
    const newCases = [...cases];
    newCases[index][field] = value;
    setCases(newCases);
  };

  const handleSave = () => {
    // Validação
    if (!variable.trim()) {
      alert("Por favor, defina a variável a ser avaliada");
      return;
    }

    const validCases = cases.filter(c => c.value.trim());
    if (validCases.length === 0) {
      alert("Por favor, adicione pelo menos um case com valor");
      return;
    }

    const formData = {
      variable: variable.trim(),
      cases: validCases.map(c => ({
        value: c.value.trim(),
        label: c.label.trim() || c.value.trim(),
      })),
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: formData,
      });
    } else {
      onSave(formData);
    }
    handleClose();
  };

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <AccountTree className={classes.icon} />
          <Typography variant="h6">
            {open === "edit" ? "Editar Switch" : "Novo Switch"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content} dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          O Switch avalia uma variável e direciona o fluxo para o case correspondente.
          Cada case tem sua própria saída no nó.
        </Typography>

        {/* Variável */}
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Variável a ser avaliada
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: ${messageType} ou ${status}"
            value={variable}
            onChange={(e) => setVariable(e.target.value)}
            variant="outlined"
            size="small"
          />
          <Typography className={classes.helperText}>
            Use {"${variavel}"} para usar variáveis do fluxo. Ex: {"${messageType}"}, {"${channel}"}
          </Typography>
        </Box>

        <Divider style={{ margin: "20px 0" }} />

        {/* Cases */}
        <Typography variant="subtitle2" gutterBottom>
          Cases (valores possíveis)
        </Typography>

        {cases.map((caseItem, index) => (
          <Paper key={index} className={classes.caseItem} elevation={0}>
            <div className={classes.caseHeader}>
              <span className={classes.caseNumber}>
                <DragIndicator style={{ width: 18, height: 18, opacity: 0.5 }} />
                Case {index + 1}
              </span>
              {cases.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveCase(index)}
                  style={{ color: "#f44336" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor"
                  placeholder="Ex: text, image, audio"
                  value={caseItem.value}
                  onChange={(e) => handleCaseChange(index, "value", e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Label (opcional)"
                  placeholder="Ex: Texto, Imagem"
                  value={caseItem.label}
                  onChange={(e) => handleCaseChange(index, "label", e.target.value)}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Paper>
        ))}

        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddCase}
          style={{
            borderColor: "#E91E63",
            color: "#E91E63",
            marginTop: "8px",
          }}
        >
          Adicionar Case
        </Button>

        {/* Default */}
        <div className={classes.defaultCase}>
          <Typography variant="subtitle2" style={{ color: "#E65100" }}>
            Default (padrão)
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Se nenhum case corresponder, o fluxo seguirá pela saída "default".
          </Typography>
        </div>

        {/* Preview */}
        <Paper className={classes.previewBox} elevation={0}>
          <Typography variant="subtitle2" gutterBottom>
            Prévia:
          </Typography>
          <Box style={{ fontFamily: "monospace", fontSize: "12px" }}>
            <div style={{ color: "#E91E63", fontWeight: "bold" }}>
              switch ({variable || "variável"}) {"{"}
            </div>
            {cases.filter(c => c.value).map((c, i) => (
              <div key={i} style={{ paddingLeft: 16, color: "#1565C0" }}>
                case "{c.value}": → Saída {i + 1}
              </div>
            ))}
            <div style={{ paddingLeft: 16, color: "#E65100" }}>
              default: → Saída padrão
            </div>
            <div style={{ color: "#E91E63", fontWeight: "bold" }}>{"}"}</div>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          style={{ backgroundColor: "#E91E63", color: "#fff" }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddSwitchModal;
